import api from "api";
import {ICandidate} from "api/types";
import {useQuery, useMutation, useQueryClient} from "@tanstack/react-query";
import {useCallback, useState} from "react";

const useCandidates = () => {
  const queryClient = useQueryClient();
  const [changes, setChanges] = useState<
    Record<string, {state?: ICandidate["state"]; inactive?: boolean; note?: string}>
  >({});

  const {data: candidates} = useQuery({
    queryKey: ["candidates"],
    queryFn: () => api.getCandidates(),
    staleTime: 5 * 60 * 1000,
  });

  const setCandidates = useCallback(
    (updater: ICandidate[] | ((old: ICandidate[] | undefined) => ICandidate[] | undefined)) => {
      queryClient.setQueryData<ICandidate[]>(["candidates"], updater);
    },
    [queryClient],
  );

  const {mutate: _patch, isPending: patching} = useMutation({
    mutationFn: ({changes, newOrder}: {changes: Record<string, any>; newOrder?: string[]}) =>
      api.updateCandidates({changes, newOrder}),
    onSuccess: () => setChanges({}),
  });

  const patchUpdates = useCallback(
    (patchOrder = true) => {
      if (patching) return;
      const currentCandidates = queryClient.getQueryData<ICandidate[]>(["candidates"]);
      _patch({
        changes,
        newOrder: patchOrder ? currentCandidates?.map((c) => c.id) : undefined,
      });
    },
    [patching, changes, _patch, queryClient],
  );

  return {
    candidates,
    setCandidates,
    setChanges,
    patchUpdates,
    patching,
  };
};

export default useCandidates;
